<template>
  <component :is="wrapperIs" v-bind="wrapperBind">
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-width="embedded ? '128px' : '120px'"
      size="medium"
      :class="['lead-form', { 'lead-form--embedded': embedded, 'form-page-form': !embedded }]"
    >
      <el-form-item v-if="isEdit" :label="$t('leadFormPage.leadNo')">
        <el-input v-model="form.leadNo" disabled />
      </el-form-item>

      <template v-if="!embedded && !isEdit">
        <el-divider content-position="left">{{ $t('common.contactSection') }}</el-divider>
      </template>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('common.firstName')" prop="firstName">
            <el-input v-model="form.firstName" :placeholder="$t('leadFormPage.firstNamePh')" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('common.lastName')">
            <el-input v-model="form.lastName" :placeholder="$t('leadFormPage.lastNamePh')" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('grid.email')" prop="email">
            <el-input v-model="form.email" :placeholder="$t('leadFormPage.emailPh')" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('grid.phone')">
            <el-input v-model="form.phone" :placeholder="$t('leadFormPage.phonePh')" clearable />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="24">
          <el-form-item :label="$t('leadFormPage.addressLabel')">
            <div v-if="mapsKeyPresent" ref="addressPlaceHost" class="lead-form__gmp-host" />
            <el-input
              v-else
              ref="addressInput"
              v-model="form.addressText"
              maxlength="1000"
              show-word-limit
              :placeholder="$t('leadFormPage.addressPh')"
              clearable
            />
            <p v-if="!mapsKeyPresent" class="lead-form__address-hint">{{ $t('leadFormPage.addressManualHint') }}</p>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="24">
          <template v-if="!embedded && !isEdit">
            <el-divider content-position="left">{{ $t('common.sourceSection') }}</el-divider>
          </template>
          <el-form-item :label="$t('common.source')">
            <div
              class="lead-form-source-row"
              :class="{ 'lead-form-source-row--split': sourcePreset === leadSourceOther }"
            >
              <el-select
                v-model="sourcePreset"
                clearable
                class="lead-form-source-select"
                :placeholder="$t('leadFormPage.sourcePresetPh')"
                @change="onSourcePresetChange"
              >
                <el-option
                  v-for="opt in sourcePresetSelectOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
              <el-input
                v-if="sourcePreset === leadSourceOther"
                v-model="sourceOtherText"
                clearable
                maxlength="500"
                class="lead-form-source-other-input"
                :placeholder="$t('leadFormPage.sourceOtherDetail')"
              />
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="embedGutter">
        <el-col :span="24">
          <el-form-item :label="$t('leadFormPage.descriptionLabel')">
            <el-input
              v-model="form.descriptionText"
              type="textarea"
              :rows="3"
              maxlength="2000"
              show-word-limit
              :placeholder="$t('leadFormPage.descriptionPh')"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row v-if="isOffice" :gutter="embedded ? 16 : embedGutter">
        <el-col :span="12">
          <el-form-item :label="$t('common.assignSales')">
            <el-select
              v-model="form.assignedSalesUserId"
              clearable
              filterable
              :placeholder="$t('leadFormPage.salesOwnerPh')"
              style="width:100%;"
            >
              <el-option v-for="s in salesList" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('leadListPage.appointmentDate')" prop="appointmentDate">
            <el-date-picker
              v-model="form.appointmentDate"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="$t('common.selectDate')"
              style="width:100%;"
              :picker-options="appointmentPickerOptions"
              @change="onAppointmentChange"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item v-else :label="$t('leadListPage.appointmentDate')" prop="appointmentDate">
        <el-date-picker
          v-model="form.appointmentDate"
          type="date"
          value-format="yyyy-MM-dd"
          :placeholder="$t('common.selectDate')"
          style="width:100%;"
          :picker-options="appointmentPickerOptions"
          @change="onAppointmentChange"
        />
      </el-form-item>

      <template v-if="!embedded">
        <el-divider content-position="left">{{ $t('leadFormPage.visitDivider') }}</el-divider>
        <el-alert
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom:12px;"
          :title="$t('leadFormPage.visitAlert')"
        />
      </template>
      <p v-else class="lead-form__hint">{{ $t('leadFormPage.visitAlert') }}</p>

      <template v-if="isEdit && embedded">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item :label="$t('leadFormPage.start')">
              <el-cascader
                :value="visitStartCascadeValue"
                :options="visitCascaderOptions"
                :props="visitCascaderFieldProps"
                :disabled="visitTimeSelectDisabled"
                clearable
                filterable
                :separator="$t('leadFormPage.visitCascadeSeparator')"
                :placeholder="$t('leadFormPage.visitStart')"
                popper-class="lead-visit-cascade-popper"
                class="visit-cascade-control"
                @change="onVisitStartCascadeChange"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('leadFormPage.end')">
              <el-cascader
                :value="visitEndCascadeValue"
                :options="visitCascaderOptions"
                :props="visitCascaderFieldProps"
                :disabled="visitTimeSelectDisabled"
                clearable
                filterable
                :separator="$t('leadFormPage.visitCascadeSeparator')"
                :placeholder="$t('leadFormPage.visitEnd')"
                popper-class="lead-visit-cascade-popper"
                class="visit-cascade-control"
                @change="onVisitEndCascadeChange"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
      <template v-else-if="isEdit && !embedded">
        <el-form-item :label="$t('leadFormPage.visitOptional')">
          <div class="visit-row">
            <span class="visit-lbl">{{ $t('leadFormPage.start') }}</span>
            <el-cascader
              :value="visitStartCascadeValue"
              :options="visitCascaderOptions"
              :props="visitCascaderFieldProps"
              :disabled="visitTimeSelectDisabled"
              clearable
              filterable
              :separator="$t('leadFormPage.visitCascadeSeparator')"
              :placeholder="$t('leadFormPage.visitStart')"
              popper-class="lead-visit-cascade-popper"
              class="visit-cascade-control visit-select-grow"
              @change="onVisitStartCascadeChange"
            />
          </div>
          <div class="visit-row" style="margin-top:8px;">
            <span class="visit-lbl">{{ $t('leadFormPage.end') }}</span>
            <el-cascader
              :value="visitEndCascadeValue"
              :options="visitCascaderOptions"
              :props="visitCascaderFieldProps"
              :disabled="visitTimeSelectDisabled"
              clearable
              filterable
              :separator="$t('leadFormPage.visitCascadeSeparator')"
              :placeholder="$t('leadFormPage.visitEnd')"
              popper-class="lead-visit-cascade-popper"
              class="visit-cascade-control visit-select-grow"
              @change="onVisitEndCascadeChange"
            />
          </div>
        </el-form-item>
      </template>
      <el-row v-else :gutter="embedGutter">
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('leadFormPage.visitStart')">
            <el-cascader
              :value="visitStartCascadeValue"
              :options="visitCascaderOptions"
              :props="visitCascaderFieldProps"
              :disabled="visitTimeSelectDisabled"
              clearable
              filterable
              :separator="$t('leadFormPage.visitCascadeSeparator')"
              :placeholder="$t('leadFormPage.visitStartPh')"
              popper-class="lead-visit-cascade-popper"
              class="visit-cascade-control"
              @change="onVisitStartCascadeChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="embedColHalf">
          <el-form-item :label="$t('leadFormPage.visitEnd')">
            <el-cascader
              :value="visitEndCascadeValue"
              :options="visitCascaderOptions"
              :props="visitCascaderFieldProps"
              :disabled="visitTimeSelectDisabled"
              clearable
              filterable
              :separator="$t('leadFormPage.visitCascadeSeparator')"
              :placeholder="$t('leadFormPage.visitEndPh')"
              popper-class="lead-visit-cascade-popper"
              class="visit-cascade-control"
              @change="onVisitEndCascadeChange"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item v-if="!embedded" class="form-page-form__actions">
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('common.save') }}</el-button>
        <el-button @click="$router.push('/lead/list')">{{ $t('common.backToList') }}</el-button>
      </el-form-item>
    </el-form>
  </component>
</template>

<script>
import { createLead, updateLead, getLeadDetail } from '@/api/lead'
import { getSalesUserList } from '@/api/user'
import { LEAD_SOURCE_OTHER, LEAD_SOURCE_PRESET_VALUES } from '@/constants/leadSource'
import FormPageShell from '@/components/FormPageShell.vue'

export default {
  name: 'LeadForm',
  components: { FormPageShell },
  props: {
    embedded: { type: Boolean, default: false },
    recordId: { type: [String, Number], default: null }
  },
  data() {
    return {
      isEdit: false,
      isOffice: false,
      appointmentTouched: false,
      visitTouched: false,
      leadSourceOther: LEAD_SOURCE_OTHER,
      sourcePreset: '',
      sourceOtherText: '',
      saving: false,
      salesList: [],
      visitStartHm: null,
      visitEndHm: null,
      visitStartPeriod: null,
      visitEndPeriod: null,
      visitCascaderFieldProps: {
        expandTrigger: 'hover',
        emitPath: true
      },
      addressPlaceAutocompleteEl: null,
      addressGmpSelectHandler: null,
      addressGmpInputHandler: null,
      form: {
        leadNo: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        addressText: '',
        sourceText: '',
        descriptionText: '',
        leadStatusCode: 1,
        assignedSalesUserId: null,
        appointmentDate: null
      },
      appointmentPickerOptions: {
        disabledDate: (time) => {
          const d = new Date(time.getFullYear(), time.getMonth(), time.getDate())
          const today = new Date()
          const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate())
          return d.getTime() < t0.getTime()
        }
      }
    }
  },
  computed: {
    embedGutter() {
      return this.embedded ? 16 : 0
    },
    embedColHalf() {
      return this.embedded ? 12 : 24
    },
    mapsKeyPresent() {
      const k = process.env.VUE_APP_GOOGLE_MAPS_API_KEY
      return !!(k && String(k).trim())
    },
    visitTimeSelectDisabled() {
      return !this.form.appointmentDate
    },
    visitCascaderOptions() {
      void this.$locale
      const morningLeaves = this.visitChoicesForHourSpan(0, 11).map((o) => ({
        value: o.value,
        label: o.label
      }))
      const afternoonLeaves = this.visitChoicesForHourSpan(12, 23).map((o) => ({
        value: o.value,
        label: o.label
      }))
      return [
        {
          value: 'morning',
          label: this.$t('leadFormPage.visitPeriodMorning'),
          children: morningLeaves
        },
        {
          value: 'afternoon',
          label: this.$t('leadFormPage.visitPeriodAfternoon'),
          children: afternoonLeaves
        }
      ]
    },
    visitStartCascadeValue() {
      if (!this.visitStartHm) return []
      const p =
        this.visitStartPeriod && this.hmMatchesPeriod(this.visitStartHm, this.visitStartPeriod)
          ? this.visitStartPeriod
          : this.periodFromHm(this.visitStartHm)
      return p && this.visitStartHm ? [p, this.visitStartHm] : []
    },
    visitEndCascadeValue() {
      if (!this.visitEndHm) return []
      const p =
        this.visitEndPeriod && this.hmMatchesPeriod(this.visitEndHm, this.visitEndPeriod)
          ? this.visitEndPeriod
          : this.periodFromHm(this.visitEndHm)
      return p && this.visitEndHm ? [p, this.visitEndHm] : []
    },
    wrapperIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    wrapperBind() {
      if (this.embedded) {
        return { class: 'lead-form__embed' }
      }
      return {
        isEdit: this.isEdit,
        title: this.isEdit ? this.$t('leadFormPage.editTitle') : this.$t('leadFormPage.newTitle'),
        subtitle: this.isEdit ? '' : this.$t('leadFormPage.newSubtitle'),
        icon: 'el-icon-user-solid',
        theme: 'primary',
        wide: true
      }
    },
    sourcePresetSelectOptions() {
      void this.$locale
      const labelKey = {
        Yelp: 'leadFormPage.sourceYelp',
        官网: 'leadFormPage.sourceOfficialSite',
        谷歌: 'leadFormPage.sourceGoogle',
        Meta: 'leadFormPage.sourceMeta',
        杂志: 'leadFormPage.sourceMagazine'
      }
      const presets = LEAD_SOURCE_PRESET_VALUES.map((value) => ({
        value,
        label: this.$t(labelKey[value])
      }))
      return [...presets, { value: LEAD_SOURCE_OTHER, label: this.$t('leadFormPage.sourceOther') }]
    },
    rules() {
      return {
        firstName: [{ required: true, message: this.$t('common.required'), trigger: 'blur' }],
        email: [
          {
            validator: (rule, value, callback) => {
              const v = value != null ? String(value).trim() : ''
              if (!v) return callback()
              const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
              return ok ? callback() : callback(new Error(this.$t('leadFormPage.emailInvalid')))
            },
            trigger: ['blur', 'change']
          }
        ],
        appointmentDate: [
          {
            validator: (rule, value, callback) => {
              if (value == null || value === '') {
                callback()
                return
              }
              if (this.isEdit) {
                callback()
                return
              }
              const parts = String(value).split('-')
              if (parts.length !== 3) {
                callback()
                return
              }
              const picked = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
              const today = new Date()
              const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate())
              if (picked.getTime() < t0.getTime()) {
                callback(new Error(this.$t('leadFormPage.apptPast')))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      }
    }
  },
  async created() {
    const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
    this.isOffice = roles.includes('OFFICE') || roles.includes('ADMIN')

    if (this.embedded) {
      const rid = this.recordId
      this.isEdit = rid != null && rid !== ''
      if (this.isOffice) await this.loadSales()
      if (this.isEdit) {
        try {
          await this.loadDetail(rid)
        } catch (e) {
          this.$emit('cancel')
        }
      } else if (!this.isOffice) {
        this.$message.warning(this.$t('leadFormPage.officeOnly'))
        this.$emit('cancel')
      }
      return
    }

    if (this.isOffice) {
      await this.loadSales()
    }
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      await this.loadDetail(id)
    } else if (!this.isOffice) {
      this.$message.warning(this.$t('leadFormPage.officeOnly'))
      this.$router.replace('/lead/list')
    }
  },
  mounted() {
    this.$nextTick(() => this.setupAddressAutocomplete())
  },
  beforeDestroy() {
    this.teardownAddressAutocomplete()
  },
  methods: {
    applySourceFieldsFromStoredSourceText() {
      const raw = (this.form.sourceText != null ? String(this.form.sourceText) : '').trim()
      if (!raw) {
        this.sourcePreset = ''
        this.sourceOtherText = ''
        return
      }
      if (LEAD_SOURCE_PRESET_VALUES.includes(raw)) {
        this.sourcePreset = raw
        this.sourceOtherText = ''
      } else {
        this.sourcePreset = LEAD_SOURCE_OTHER
        this.sourceOtherText = raw
      }
    },
    onSourcePresetChange() {
      if (this.sourcePreset !== LEAD_SOURCE_OTHER) {
        this.sourceOtherText = ''
      }
    },
    syncSourceTextFromFields() {
      if (this.sourcePreset === LEAD_SOURCE_OTHER) {
        this.form.sourceText = (this.sourceOtherText || '').trim()
      } else if (this.sourcePreset) {
        this.form.sourceText = this.sourcePreset
      } else {
        this.form.sourceText = ''
      }
    },
    editId() {
      if (this.embedded) return this.recordId
      return this.$route.params.id
    },
    async loadSales() {
      try {
        const resp = await getSalesUserList()
        const rows = (resp && resp.list) || []
        this.salesList = rows.map(r => ({
          value: r.id,
          label: (r.realName || r.username || '').trim() || this.$t('leadListPage.unnamed')
        }))
      } catch (e) {
        this.salesList = []
      }
    },
    async loadDetail(id) {
      const resp = await getLeadDetail(id)
      const lead = resp.lead
      if (!lead) {
        this.$message.error(this.$t('common.loadFailed'))
        throw new Error('no lead')
      }
      let appointmentDate = lead.appointmentDate || null
      let visitStartHm = null
      let visitEndHm = null
      if (lead.visitStartAt) {
        const dp = String(lead.visitStartAt).substring(0, 10)
        if (!appointmentDate && /^\d{4}-\d{2}-\d{2}$/.test(dp)) {
          appointmentDate = dp
        }
        visitStartHm = this.extractHmFromVisitAt(lead.visitStartAt)
      }
      if (lead.visitEndAt) {
        visitEndHm = this.extractHmFromVisitAt(lead.visitEndAt)
      }
      this.form = {
        leadNo: lead.leadNo,
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        addressText: lead.addressText || '',
        sourceText: lead.sourceText,
        descriptionText: lead.descriptionText,
        leadStatusCode: lead.leadStatusCode,
        assignedSalesUserId: lead.assignedSalesUserId,
        appointmentDate
      }
      this.visitStartHm = visitStartHm
      this.visitEndHm = visitEndHm
      this.visitStartPeriod = visitStartHm ? this.periodFromHm(visitStartHm) : null
      this.visitEndPeriod = visitEndHm ? this.periodFromHm(visitEndHm) : null
      this.applySourceFieldsFromStoredSourceText()
      this.appointmentTouched = false
      this.visitTouched = false
      this.$nextTick(() => this.setupAddressAutocomplete())
    },
    onAppointmentChange() {
      this.appointmentTouched = true
      if (!this.form.appointmentDate) {
        this.visitStartHm = null
        this.visitEndHm = null
        this.visitStartPeriod = null
        this.visitEndPeriod = null
        this.visitTouched = true
      }
    },
    visitChoicesForHourSpan(hLow, hHigh) {
      void this.$locale
      const localeStr = String(this.$locale || 'en-US')
      const intlLocale = localeStr.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'
      const pad = n => String(n).padStart(2, '0')
      const labelForHm = (hm) => {
        const [h, mi] = hm.split(':').map(Number)
        const d = new Date(2000, 0, 1, h, mi, 0, 0)
        return new Intl.DateTimeFormat(intlLocale, { hour: 'numeric', minute: '2-digit', hour12: true }).format(d)
      }
      const out = []
      for (let h = hLow; h <= hHigh; h++) {
        for (const m of [0, 15, 30, 45]) {
          const value = `${pad(h)}:${pad(m)}`
          out.push({ value, label: labelForHm(value) })
        }
      }
      return out
    },
    periodFromHm(hm) {
      if (hm == null || hm === '') return null
      const head = parseInt(String(hm).split(':')[0], 10)
      if (Number.isNaN(head)) return null
      return head < 12 ? 'morning' : 'afternoon'
    },
    hmMatchesPeriod(hm, period) {
      if (!period || hm == null || hm === '') return false
      const head = parseInt(String(hm).split(':')[0], 10)
      if (Number.isNaN(head)) return false
      return period === 'morning' ? head < 12 : head >= 12
    },
    applyDefaultVisitEnd() {
      if (!this.visitStartHm || !this.form.appointmentDate) return
      this.visitEndHm = this.computeDefaultEndHm(this.form.appointmentDate, this.visitStartHm)
      this.visitEndPeriod = this.visitEndHm ? this.periodFromHm(this.visitEndHm) : null
    },
    onVisitStartCascadeChange(val) {
      this.visitTouched = true
      if (!val || val.length === 0) {
        this.visitStartPeriod = null
        this.visitStartHm = null
        this.visitEndHm = null
        this.visitEndPeriod = null
        return
      }
      if (val.length < 2) return
      this.visitStartPeriod = val[0]
      this.visitStartHm = val[1]
      if (!this.form.appointmentDate) return
      this.applyDefaultVisitEnd()
    },
    onVisitEndCascadeChange(val) {
      this.visitTouched = true
      if (!val || val.length === 0) {
        this.visitEndPeriod = null
        this.visitEndHm = null
        return
      }
      if (val.length < 2) return
      this.visitEndPeriod = val[0]
      this.visitEndHm = val[1]
    },
    snapHmToQuarter(h, mi) {
      let t = h * 60 + mi
      t = Math.round(t / 15) * 15
      if (t < 0) t = 0
      if (t >= 24 * 60) t = 23 * 60 + 45
      const hh = Math.floor(t / 60)
      const mm = t % 60
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
    },
    extractHmFromVisitAt(iso) {
      if (iso == null || iso === '') return null
      const s = String(iso).trim()
      const m = s.match(/T(\d{2}):(\d{2})/)
      if (!m) return null
      return this.snapHmToQuarter(+m[1], +m[2])
    },
    buildVisitIso(appointmentDateStr, hm) {
      if (!appointmentDateStr || !hm) return null
      const parts = String(hm).split(':')
      const hh = parts[0]
      const mm = parts[1] || '00'
      return `${appointmentDateStr}T${hh}:${mm}:00`
    },
    computeDefaultEndHm(apptDateStr, startHm) {
      const [y, mo, d] = apptDateStr.split('-').map(Number)
      const [sh, sm] = startHm.split(':').map(Number)
      const start = new Date(y, mo - 1, d, sh, sm, 0)
      let end = new Date(start.getTime() + 60 * 60 * 1000)
      const sameCal = (a, b) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
      if (!sameCal(end, start)) {
        end = new Date(y, mo - 1, d, 23, 45, 0)
      }
      let totalMin = end.getHours() * 60 + end.getMinutes()
      totalMin = Math.round(totalMin / 15) * 15
      const cap = 23 * 60 + 45
      if (totalMin > cap) totalMin = cap
      const startMin = sh * 60 + sm
      if (totalMin <= startMin) totalMin = Math.min(startMin + 15, cap)
      const eh = Math.floor(totalMin / 60)
      const emi = totalMin % 60
      return `${String(eh).padStart(2, '0')}:${String(emi).padStart(2, '0')}`
    },
    /** 解析本地时间的 yyyy-MM-ddTHH:mm:ss */
    parseVisitLocal(s) {
      if (s == null || s === '') return null
      const m = String(s).trim().match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/)
      if (!m) return null
      const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])
      return Number.isNaN(d.getTime()) ? null : d
    },
    resolveVisitPayload() {
      const apt = this.form.appointmentDate
      const sh = this.visitStartHm
      const eh = this.visitEndHm
      if (!sh && !eh) return { empty: true }
      if (!apt) return { error: this.$t('leadFormPage.visitNeedAppointmentDate') }
      if (!sh || !eh) return { error: this.$t('leadFormPage.visitNeedBothTimes') }
      const visitStartAt = this.buildVisitIso(apt, sh)
      const visitEndAt = this.buildVisitIso(apt, eh)
      const ds = this.parseVisitLocal(visitStartAt)
      const de = this.parseVisitLocal(visitEndAt)
      if (!ds || !de) return { error: this.$t('common.failed') }
      if (de.getTime() <= ds.getTime()) return { error: this.$t('leadFormPage.visitEndPh') }
      return { empty: false, visitStartAt, visitEndAt }
    },
    async setupAddressAutocomplete() {
      if (!this.mapsKeyPresent) return
      this.teardownAddressAutocomplete()
      await this.$nextTick()
      const host = this.$refs.addressPlaceHost
      if (!host) return
      try {
        const { Loader } = await import('@googlemaps/js-api-loader')
        const loader = new Loader({
          apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
          version: 'weekly'
        })
        await loader.load()
        if (this._isDestroyed) return
        const placesLib = await window.google.maps.importLibrary('places')
        const Pace = placesLib.PlaceAutocompleteElement
        if (!Pace) throw new Error('PlaceAutocompleteElement missing')
        const el = new Pace({
          requestedLanguage: String(this.$locale || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en'
        })
        el.classList.add('lead-form-gmp-place')
        el.maxlength = 1000
        el.noInputIcon = true
        el.placeholder = this.$t('leadFormPage.addressPh')
        host.appendChild(el)

        const onSelect = async (event) => {
          const pred = event && event.placePrediction
          if (!pred) return
          let place = pred.toPlace()
          if (place && typeof place.then === 'function') place = await place
          if (!place || typeof place.fetchFields !== 'function') return
          await place.fetchFields({ fields: ['formattedAddress', 'displayName'] })
          const addr = place.formattedAddress || place.displayName
          if (addr) this.form.addressText = addr
        }

        el.addEventListener('gmp-select', onSelect)

        await new Promise(resolve => window.requestAnimationFrame(resolve))
        const inner = el.input
        const syncValue = () => {
          this.form.addressText = el.value || ''
        }
        let inputTarget = null
        if (inner && typeof inner.addEventListener === 'function') {
          inputTarget = inner
        } else if (typeof el.addEventListener === 'function') {
          inputTarget = el
        }
        if (inputTarget) {
          inputTarget.addEventListener('input', syncValue)
        }

        el.value = this.form.addressText || ''

        this.addressPlaceAutocompleteEl = el
        this.addressGmpSelectHandler = onSelect
        this.addressGmpInputHandler =
          inputTarget != null ? { target: inputTarget, handler: syncValue } : null
      } catch (e) {
        console.warn('Google Places init failed', e)
      }
    },
    teardownAddressAutocomplete() {
      const el = this.addressPlaceAutocompleteEl
      if (el) {
        if (this.addressGmpSelectHandler) {
          el.removeEventListener('gmp-select', this.addressGmpSelectHandler)
        }
        if (
          this.addressGmpInputHandler &&
          this.addressGmpInputHandler.target &&
          this.addressGmpInputHandler.handler
        ) {
          this.addressGmpInputHandler.target.removeEventListener(
            'input',
            this.addressGmpInputHandler.handler
          )
        }
        if (el.parentNode) el.parentNode.removeChild(el)
      }
      this.addressPlaceAutocompleteEl = null
      this.addressGmpSelectHandler = null
      this.addressGmpInputHandler = null
    },
    submit() {
      this.syncSourceTextFromFields()
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        this.$emit('saving', true)
        try {
          if (this.isEdit) {
            const id = this.editId()
            const payload = { ...this.form }
            if (this.appointmentTouched) {
              payload.appointmentUpdate = true
              if (!this.form.appointmentDate) {
                payload.appointmentDate = null
                payload.clearAppointmentDate = true
              } else {
                payload.clearAppointmentDate = false
              }
            }
            if (this.visitTouched) {
              payload.visitWindowUpdate = true
              const visit = this.resolveVisitPayload()
              if (visit.error) {
                this.$message.error(visit.error)
                return
              }
              if (visit.empty) {
                payload.clearVisitWindow = true
              } else {
                payload.clearVisitWindow = false
                payload.visitStartAt = visit.visitStartAt
                payload.visitEndAt = visit.visitEndAt
              }
            }
            delete payload.leadStatusCode
            if (!this.isOffice) {
              delete payload.assignedSalesUserId
            }
            await updateLead(id, payload)
            this.$message.success(this.$t('leadFormPage.saved'))
            if (this.embedded) {
              this.$emit('saved', { id })
            } else {
              this.$router.push('/lead/detail/' + id)
            }
          } else {
            const visit = this.resolveVisitPayload()
            if (visit.error) {
              this.$message.error(visit.error)
              return
            }
            const payload = { ...this.form }
            delete payload.leadNo
            delete payload.leadStatusCode
            if (!visit.empty) {
              payload.visitStartAt = visit.visitStartAt
              payload.visitEndAt = visit.visitEndAt
            }
            const resp = await createLead(payload)
            this.$message.success(this.$t('leadFormPage.created'))
            const newId = resp.leadId || (resp.data && resp.data.leadId)
            if (this.embedded) {
              this.$emit('saved', { id: newId })
            } else if (newId) {
              this.$router.push('/lead/detail/' + newId)
            } else {
              this.$router.push('/lead/list')
            }
          }
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('common.failed'))
        } finally {
          this.saving = false
          this.$emit('saving', false)
        }
      })
    }
  }
}
</script>

<style scoped>
.form-page-form {
  max-width: 720px;
}

.lead-form--embedded {
  max-width: none;
}

.lead-form__embed {
  min-width: 0;
}

.lead-form__hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: var(--dark-text-sub, #8b95a1);
  line-height: 1.45;
}

.form-page-form__actions {
  margin-top: 8px;
  margin-bottom: 0;
}

.visit-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.visit-cascade-control {
  width: 100%;
}

.visit-select-grow {
  flex: 1;
  min-width: 0;
}

.lead-form__gmp-host {
  width: 100%;
  min-width: 0;
  min-height: 36px;
}

.visit-lbl {
  width: 44px;
  flex-shrink: 0;
  color: var(--dark-text-sub, #8b95a1);
  font-size: 13px;
}

.lead-form-source-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.lead-form-source-row .lead-form-source-select {
  width: 100%;
}

.lead-form-source-row--split .lead-form-source-select {
  flex: 1;
  min-width: 0;
  width: auto !important;
  max-width: none;
}

.lead-form-source-row--split .lead-form-source-other-input {
  flex: 1;
  min-width: 0;
}
</style>

<style>
/* 下拉层挂到 body，需非 scoped；双列：上午/下午 + 具体时间 */
.lead-visit-cascade-popper .el-cascader-menu {
  min-width: 128px;
}

.lead-visit-cascade-popper .el-scrollbar__wrap {
  max-height: 280px;
}
</style>
